import React, { useEffect, useState } from "react";
import { Packages as PackagesApi, Leads, Pages } from "@/lib/api";
import { Link } from "react-router-dom";
import { Boxes, Inbox, FileText, ArrowUpRight } from "lucide-react";

const Stat = ({ label, value, icon: Icon, to, testId }) => (
  <Link
    to={to}
    className="glass glass-hover rounded-2xl p-6 flex items-center justify-between group"
    data-testid={testId}
  >
    <div>
      <div className="text-xs uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className="font-display font-black text-3xl text-white mt-2">{value}</div>
    </div>
    <div className="h-12 w-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
      <Icon className="h-5 w-5 text-slate-300 group-hover:text-atc-blue transition" />
    </div>
  </Link>
);

const Dashboard = () => {
  const [stats, setStats] = useState({ packages: 0, leads: 0, pages: 0, newLeads: 0 });
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      const [pkgs, leads, pages] = await Promise.all([
        PackagesApi.list().catch(() => []),
        Leads.list().catch(() => []),
        Pages.list().catch(() => []),
      ]);
      setStats({
        packages: pkgs.length,
        leads: leads.length,
        pages: pages.length,
        newLeads: leads.filter((l) => l.status === "new").length,
      });
      setRecent(leads.slice(0, 5));
    })();
  }, []);

  return (
    <div className="p-10" data-testid="admin-dashboard">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-atc-blue mb-2">Dashboard</div>
          <h1 className="font-display font-black text-4xl text-white">Welcome back.</h1>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <Stat label="New Leads" value={stats.newLeads} icon={Inbox} to="/admin/leads" testId="stat-new-leads" />
        <Stat label="Total Leads" value={stats.leads} icon={Inbox} to="/admin/leads" testId="stat-total-leads" />
        <Stat label="Packages" value={stats.packages} icon={Boxes} to="/admin/packages" testId="stat-packages" />
        <Stat label="CMS Pages" value={stats.pages} icon={FileText} to="/admin/pages" testId="stat-pages" />
      </div>

      <div className="mt-10 glass rounded-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="font-display font-bold text-lg text-white">Recent leads</div>
          <Link to="/admin/leads" className="text-xs text-atc-blue inline-flex items-center gap-1 hover:gap-2 transition-all">
            View all <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        {recent.length === 0 ? (
          <div className="text-sm text-slate-500 py-8 text-center">No leads yet — they'll show up here.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {recent.map((l) => (
              <div key={l.id} className="py-4 flex items-center justify-between gap-4">
                <div>
                  <div className="text-sm text-white font-medium">{l.name}</div>
                  <div className="text-xs text-slate-400">{l.email} · {l.service || "—"}</div>
                </div>
                <div className="text-xs text-slate-500">{new Date(l.created_at).toLocaleDateString()}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
